import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import EmployeeIntegrationsSync from "@/components/integrations/employee/employee-integrations-sync";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import AvatarCompany from "@/components/company/avatar-company";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { convertDateToDistanceNow } from "@/lib/date";
import { toSearchableString } from "@/lib/string";
import { CompanyDTO, EmployeeDTO, SearchEngineEmployeeResult } from "@shaarp/common";
import { ColumnDef } from "@tanstack/react-table";
import { RiLinkedinFill } from "react-icons/ri";
import { Link } from "react-router-dom";
import EmployeeListingsSelector from "../employee/employee-listings-selector";
import EmployeeUserAssignedSelector from "../employee/employee-user-assigned-selector";
import { EmployeeCompanyLink } from "@/components/data-table-company-employees/employee-company-link";
import DataTableCellCompanyEmployeeEmail from "./data-table-cell-company-employee-email";
import DataTableCellCompanyEmployeePhone from "./data-table-cell-company-employee-phone";

export const dataTableEmployeesColumns = (
  companyId: string | null,
  onRowChanged: (
    update: Partial<EmployeeDTO> & { id: string }
  ) => void = () => {},
  showIntegrationsColumn: boolean = false,
  onRemoveFromListing?: (employee: EmployeeDTO) => void,
  t: (key: string) => string = () => "",
  currentLanguage: "en" | "fr" = "en",
  showAssignedColumn: boolean = true,
  forceContactActions: boolean = false,
  showEmailColumn: boolean = true,
  showPhoneColumn: boolean = true,
  /**
   * Shows the reveal buttons disabled and leaves out the CRM sync, the lists
   * and the assignment: rows about to be replaced, where none of them would
   * last (`DataTableCompanyEmployees`'s `areRowActionsLocked`).
   */
  areRowActionsLocked: boolean = false,
) => {

  const columns = [
    {
      accessorKey: "avatar",
      header: "",
      cell: ({ row }) => {
        const employee = row.original;
        const initials =
          `${employee.firstName?.charAt(0) || ""}${employee.lastName?.charAt(0) || ""}`.toUpperCase();
        return (
          <div className="flex justify-center">
            <Avatar className="w-12 h-12">
              <AvatarImage
                src={employee.photoUrl || ""}
                alt={employee.firstName || ""}
              />
              <AvatarFallback>{initials || "?"}</AvatarFallback>
            </Avatar>
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      id: "Nom",
      accessorFn: (employee: EmployeeDTO) => {
        return `${employee.firstName} ${employee.lastName}`;
      },
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("components.dataTableCompanyEmployees.columns.name")}
        />
      ),
      cell: ({ row }) => {
        const employee = row.original;

        return (
          <span className="font-bold">
            {employee.firstName} {employee.lastName}
          </span>
        );
      },
      filterFn: (row, _id, value) => {
        const employee = row.original;
        return (
          (!!employee.firstName &&
            toSearchableString(employee.firstName).includes(
              toSearchableString(value),
            )) ||
          (!!employee.lastName &&
            toSearchableString(employee.lastName).includes(
              toSearchableString(value),
            )) ||
          (!!employee.jobTitle &&
            toSearchableString(employee.jobTitle).includes(
              toSearchableString(value),
            ))
        );
      },
    },
    {
      id: "Titre",
      accessorKey: "jobTitle",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("components.dataTableCompanyEmployees.columns.title")}
        />
      ),
    },
    ...(!companyId
      ? [
          {
            id: "Entreprise",
            accessorFn: (employee: EmployeeDTO) => {
              const e = employee as SearchEngineEmployeeResult;
              return e.companyName || e.domain || "";
            },
            header: ({ column }: { column: any }) => (
              <DataTableColumnHeader
                column={column}
                title={t("prospecting.search.employees.columns.company")}
              />
            ),
            cell: ({ row }: { row: any }) => {
              const employee = row.original as SearchEngineEmployeeResult;
              const label = employee.companyName || employee.domain || "-";
              const minimalCompany = {
                domain: employee.domain,
                name: employee.companyName,
              } as CompanyDTO;

              if (employee.companyId) {
                return (
                  <EmployeeCompanyLink
                    companyId={employee.companyId}
                    label={label}
                    company={minimalCompany}
                  />
                );
              }

              return (
                <Tooltip delayDuration={0}>
                  <TooltipTrigger asChild>
                    <span className="text-muted-foreground cursor-default flex items-center gap-2">
                      <AvatarCompany className="size-5" company={minimalCompany} />
                      {label}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    {t("components.dataTableCompanyEmployees.company.missingDataTooltip")}
                  </TooltipContent>
                </Tooltip>
              );
            },
          },
        ]
      : []),
    {
      id: "Depuis",
      accessorFn: (employee: EmployeeDTO) => {
        return employee.employedFrom ? new Date(employee.employedFrom) : null;
      },
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("components.dataTableCompanyEmployees.columns.since")}
        />
      ),
      cell: ({ row }) => {
        const employee = row.original;

        if (!employee.employedFrom) {
          return null;
        }

        const distanceNow = convertDateToDistanceNow(employee.employedFrom, {
          addSuffix: false,
          languageOverride: currentLanguage,
        });

        const employedFrom = new Date(employee.employedFrom);
        const month = employedFrom.toLocaleString("default", {
          month: "short",
        });
        const year = employedFrom.getFullYear();

        return (
          <span>
            {distanceNow}
            <br />({month} {year})
          </span>
        );
      },
    },
    {
      id: "Ville",
      accessorKey: "city",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("components.dataTableCompanyEmployees.columns.city")}
        />
      ),
    },
    {
      id: "Pays",
      accessorKey: "country",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("components.dataTableCompanyEmployees.columns.country")}
        />
      ),
      cell: ({ row }) => {
        const employee = row.original;
        return <span>{employee.country}</span>;
      },
    },
    {
      id: "LinkedIn",
      accessorKey: "linkedin_url",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("components.dataTableCompanyEmployees.columns.linkedin")}
        />
      ),
      cell: ({ row }) => {
        const employee = row.original;

        if (!employee.linkedInUrl) {
          return null;
        }

        return (
          <Button size="icon" variant="secondary" asChild>
            <Link to={employee.linkedInUrl} target="_blank">
              <RiLinkedinFill />
            </Link>
          </Button>
        );
      },
    },
    {
      id: "Email",
      accessorFn: (employee: EmployeeDTO) => {
        if (employee.email) {
          return 3;
        }
        if (employee.emailStatus !== "VERIFIED") {
          return 2;
        }
        return 1;
      },
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("components.dataTableCompanyEmployees.columns.email")}
        />
      ),
      cell: ({ row }) => {
        const employee = row.original;
        const resolvedCompanyId =
          companyId ?? (employee as EmployeeDTO & { companyId?: string }).companyId;
        if (!forceContactActions && (!resolvedCompanyId || !employee.domain)) {
          return (
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <span className="text-muted-foreground text-sm cursor-default">
                  {t("components.dataTableCompanyEmployees.email.notAvailable")}
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {t("components.dataTableCompanyEmployees.email.missingDomainTooltip")}
              </TooltipContent>
            </Tooltip>
          );
        }

        return (
          <DataTableCellCompanyEmployeeEmail
            companyId={resolvedCompanyId}
            employee={employee}
            onEmailRevealed={onRowChanged}
            forceContactActions={forceContactActions}
            isRevealLocked={areRowActionsLocked}
          />
        );
      },
    },
    {
      id: "Phone",
      accessorFn: (employee: EmployeeDTO) => {
        if (employee.phone) {
          return 3;
        }
        if (employee.phoneStatus !== "VERIFIED") {
          return 2;
        }
        return 1;
      },
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("components.dataTableCompanyEmployees.columns.phone")}
        />
      ),
      cell: ({ row }) => {
        const employee = row.original;
        const resolvedCompanyId =
          companyId ?? (employee as EmployeeDTO & { companyId?: string }).companyId;
        if (!forceContactActions && (!resolvedCompanyId || !employee.domain)) {
          return (
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <span className="text-muted-foreground text-sm cursor-default">
                  {t("components.dataTableCompanyEmployees.phone.notAvailable")}
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {t("components.dataTableCompanyEmployees.phone.missingDomainTooltip")}
              </TooltipContent>
            </Tooltip>
          );
        }

        return (
          <DataTableCellCompanyEmployeePhone
            employee={employee}
            companyId={resolvedCompanyId}
            onPhoneRevealed={onRowChanged}
            forceContactActions={forceContactActions}
            isRevealLocked={areRowActionsLocked}
          />
        );
      },
    },
    ...(showIntegrationsColumn
      ? [
          {
            id: "Integrations",
            header: ({ column }: { column: any }) => (
              <DataTableColumnHeader
                column={column}
                title={t(
                  "components.dataTableCompanyEmployees.columns.integrations",
                )}
              />
            ),
            cell: ({ row }: { row: any }) => {
              const employee = row.original;
              const resolvedCompanyId =
                companyId ??
                (employee as EmployeeDTO & { companyId?: string }).companyId;

              // In search results, allow syncing even without a resolved
              // company (the contact is created with its company name only).
              if (
                areRowActionsLocked ||
                (!resolvedCompanyId && !forceContactActions)
              ) {
                return null;
              }

              return (
                <EmployeeIntegrationsSync
                  employee={employee}
                  companyId={resolvedCompanyId}
                  onSynced={onRowChanged}
                />
              );
            },
            enableSorting: false,
            enableHiding: false,
          },
        ]
      : []),
    {
      id: "MyLists",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("components.dataTableCompanyEmployees.columns.myLists")}
        />
      ),
      cell: ({ row }: { row: any }) => {
        if (areRowActionsLocked) return null;
        const employee = row.original;
        return <EmployeeListingsSelector employee={employee} />;
      },
      enableSorting: false,
      enableHiding: false,
    },
    ...(showAssignedColumn
      ? [
          {
            id: "AssignedTo",
            header: ({ column }: { column: any }) => (
              <DataTableColumnHeader
                column={column}
                title={t("components.dataTableCompanyEmployees.columns.assignedTo")}
              />
            ),
            cell: ({ row }: { row: any }) => {
              if (areRowActionsLocked) return null;
              const employee = row.original;
              return (
                <div className="flex justify-center">
                  <EmployeeUserAssignedSelector employee={employee} />
                </div>
              );
            },
            enableSorting: false,
            enableHiding: false,
          },
        ]
      : []),
    ...(onRemoveFromListing
      ? [
          {
            id: t("listing.page.content.columns.remove"),
            header: () => null,
            cell: ({ row }: { row: any }) => {
              const employee = row.original;
              return (
                <Button
                  variant="link"
                  className="transition-colors hover:text-red-700"
                  onClick={() => onRemoveFromListing(employee)}
                >
                  {t("listing.page.content.columns.remove")}
                </Button>
              );
            },
            enableSorting: false,
            enableHiding: false,
          },
        ]
      : []),
  ] as ColumnDef<EmployeeDTO>[];

  return columns.filter((col) => {
    if (col.id === "Email") {
      return showEmailColumn;
    }
    if (col.id === "Phone") {
      return showPhoneColumn;
    }
    return true;
  });
};
